import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, KeyboardArrowUp as ArrowUpIcon, KeyboardArrowDown as ArrowDownIcon, Fullscreen as FullscreenIcon, FullscreenExit as FullscreenExitIcon } from '@mui/icons-material';
import { CustomMapping, CustomMappingField } from '../types/mapping';
import { useCommonOptions } from '../hooks/useCommonOptions';
import { useDynamicMappings } from '../hooks/useDynamicMappings';
import { dineroFinanceColumns } from '../constants/DineroContants';

type FieldOption = {
  value: string;
  label: string;
  options?: { value: string; label: string }[];
  activeExtraCondition?: boolean;
  extrColumnsOptions?: { value: string; label: string }[];
};

type CustomMappingManagerProps = {
  open: boolean;
  onClose: () => void;
  onSave: (mapping: CustomMapping) => void;
  onDelete?: (mapping: CustomMapping) => void;
  existingMappings: CustomMapping[];
  field?: string;
};

const CustomMappingManager: React.FC<CustomMappingManagerProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  existingMappings,
  field
}) => {
  const [description, setDescription] = useState('');
  const [selectedField, setSelectedField] = useState(field || '');
  const [mappingFields, setMappingFields] = useState<CustomMappingField[]>([]);
  const [editingMapping, setEditingMapping] = useState<CustomMapping | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState<CustomMapping | null>(null);
  const { currencies, paymentTerms, vatZones, customerGroups, units, entryTypeOptions, vatCodes, accountTypeOptions } = useCommonOptions();
  const { mappings } = useDynamicMappings();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isActiveExtraCondition, setIsActiveExtraCondition] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const specialValueOptions = [
    { value: '', label: 'Equal' },
    { value: 'empty', label: 'Empty' },
    { value: 'not_empty', label: 'Not Empty' },
    { value: '*', label: 'Any Value (*)' },
    { value: 'custom_wildcard', label: 'Custom Pattern' },
    { value: '>', label: 'Greater Than (>)' },
    { value: '<', label: 'Less Than (<)' },
    { value: '>=', label: 'Greater or Equal (>=)' },
    { value: '<=', label: 'Less or Equal (<=)' }
  ];

  const formControlStyles = {
    '& .MuiInputBase-root': {
      height: '28px',
      fontSize: '0.875rem',
      padding: '4px 8px'
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.875rem'
    },
    '& .MuiMenuItem-root': {
      fontSize: '0.875rem',
      minHeight: '28px',
      padding: '4px 8px'
    }
  };

  const handleAddMapping = () => {
    setMappingFields([
      ...mappingFields,
      {
        field: selectedField,
        value: '',
        mappedValue: '',
        combinedConditions: []
      },
    ]);
  };

  const handleUpdateMapping = (index: number, key: keyof CustomMappingField, value: any) => {
    const updatedFields = [...mappingFields];
    const fieldOptions = getFieldOptions().find(f => f.value === selectedField)?.options || [];

    if (key === 'value') {
      updatedFields[index] = {
        ...updatedFields[index],
        value: value as string
      };
    } else if (key === 'mappedValue') {
      const option = fieldOptions.find(opt => opt.label === value);
      updatedFields[index] = {
        ...updatedFields[index],
        mappedValue: option ? option.value : value as string,
        originalMappedValue: option ? option.value : value as string,
        mappedValueLabel: value as string
      };
    } else if (key === 'combinedConditions') {
      updatedFields[index] = {
        ...updatedFields[index],
        combinedConditions: value as { column: string; value: string }[]
      };
    } else {
      updatedFields[index] = {
        ...updatedFields[index],
        [key]: value
      };
    }

    setMappingFields(updatedFields);
  };

  const handleAddCombinedCondition = (index: number) => {
    const updatedFields = [...mappingFields];
    updatedFields[index] = {
      ...updatedFields[index],
      combinedConditions: [
        ...(updatedFields[index].combinedConditions || []),
        { column: '', value: '' }
      ]
    };
    setMappingFields(updatedFields);
  };

  const handleRemoveCombinedCondition = (index: number, conditionIndex: number) => {
    const updatedFields = [...mappingFields];
    updatedFields[index] = {
      ...updatedFields[index],
      combinedConditions: updatedFields[index].combinedConditions?.filter((_, i) => i !== conditionIndex)
    };
    setMappingFields(updatedFields);
  };

  const handleUpdateCombinedCondition = (index: number, conditionIndex: number, field: 'column' | 'value', value: string) => {
    const updatedFields = [...mappingFields];
    const conditions = [...(updatedFields[index].combinedConditions || [])];
    conditions[conditionIndex] = {
      ...conditions[conditionIndex],
      [field]: value
    };
    updatedFields[index] = {
      ...updatedFields[index],
      combinedConditions: conditions
    };
    setMappingFields(updatedFields);
  };

  const getFieldOptions = (): FieldOption[] => {
    return [
      {
        value: "vatZone_vatZoneNumber",
        label: "VAT Zone",
        options: Object.entries(mappings.vatZones).map(([value, label]) => ({
          value,
          label
        })),
        activeExtraCondition: false
      },
      {
        value: "paymentTerms_paymentTermsNumber",
        label: "Payment Terms",
        options: Object.entries(mappings.paymentTerms).map(([value, label]) => ({
          value,
          label
        })),
        activeExtraCondition: false
      },
      {
        value: "customerGroup_customerGroupNumber",
        label: "Customer Group",
        options: Object.entries(mappings.customerGroups).map(([value, label]) => ({
          value,
          label
        })),
      },
      {
        value: "unit_unitNumber",
        label: "Unit",
        options: Object.entries(mappings.units).map(([value, label]) => ({
          value,
          label
        }))
      },
      {
        value: "entryTypeNumber",
        label: "Entry Type",
        options: entryTypeOptions.map(({ value, label }) => ({
          value,
          label
        })),
        activeExtraCondition: true,
        extrColumnsOptions: dineroFinanceColumns.map(({ id, label }) => ({
          value: id,
          label
        }))
      },
      {
        value: "vatCode",
        label: "VAT Code",
        options: vatCodes.map(({ value, label }) => ({
          value,
          label
        })),
        activeExtraCondition: false
      },  
      {
        value: "accountType",
        label: "Account Type",
        options: accountTypeOptions.map(({ value, label }) => ({
          value,
          label
        })),
        activeExtraCondition: false
      },
      {
        value: "keyFigureCode",
        label: "Key Figure Code",
        options: Object.entries(mappings.keyFigureCodes).map(([value, label]) => ({
          value,
          label 
        })),
        activeExtraCondition: false
      },
      {
        value: "currency",
        label: "Currency",
        options: Object.entries(mappings.currencies).map(([value, label]) => ({
          value,
          label
        }))
      }
    ];
  };

  // Filter out fields that already have mappings
  const getAvailableFields = () => {
    const allFields = getFieldOptions();
    return allFields.filter(
      field => !existingMappings.some(mapping => mapping.field === field.value && mapping.id !== editingMapping?.id)
    );
  };

  const handleSave = async () => {
    setIsUpdating(true);
    const mapping: CustomMapping = {
      id: editingMapping?.id || Date.now().toString(),
      field: selectedField,
      description,
      mappings: mappingFields.map(field => ({
        field: field.value,
        value: field.originalMappedValue || field.mappedValue,
        extraFields: field.combinedConditions?.map(cond => cond.column),
        extraValues: field.combinedConditions?.map(cond => cond.value)
      }))
    };
    await onSave(mapping);
    setDescription('');
    setSelectedField(field || '');
    setMappingFields([]);
    setEditingMapping(null);
    setActiveTab(0);
    setIsUpdating(false);
  };

  const handleClose = () => {
    handleCancel();
    onClose();
  };

  const handleCancel = () => {
    setDescription('');
    setSelectedField(field || '');
    setMappingFields([]);
    setEditingMapping(null);
    setActiveTab(0);
    //
  }

  const handleEditMapping = (mapping: CustomMapping) => {
    setEditingMapping(mapping);
    setActiveTab(1);
  };

  const handleDeleteClick = (mapping: CustomMapping) => {
    setMappingToDelete(mapping);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (mappingToDelete && onDelete) {
      onDelete(mappingToDelete);
    }
    setDeleteConfirmOpen(false);
    setMappingToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setMappingToDelete(null);
  };

  const getFieldLabel = (field: string) => {
    const fieldOption = getFieldOptions().find(f => f.value === field);
    return fieldOption ? fieldOption.label : field;
  };

  const handleRemoveMapping = (index: number) => {
    setMappingFields(mappingFields.filter((_, i) => i !== index));
  };

  const handleMoveMappingUp = (index: number) => {
    if (index > 0) {
      const newMappingFields = [...mappingFields];
      [newMappingFields[index - 1], newMappingFields[index]] = [newMappingFields[index], newMappingFields[index - 1]];
      setMappingFields(newMappingFields);
      
      // Visual feedback
      setTimeout(() => {
        const movedRow = document.getElementById(`mapping-row-${index - 1}`);
        if (movedRow) {
          movedRow.style.backgroundColor = '#e3f2fd';
          setTimeout(() => {
            movedRow.style.backgroundColor = '';
          }, 500);
        }
      }, 50);
    }
  };

  const handleMoveMappingDown = (index: number) => {
    if (index < mappingFields.length - 1) {
      const newMappingFields = [...mappingFields];
      [newMappingFields[index], newMappingFields[index + 1]] = [newMappingFields[index + 1], newMappingFields[index]];
      setMappingFields(newMappingFields);
      
      // Visual feedback
      setTimeout(() => {
        const movedRow = document.getElementById(`mapping-row-${index + 1}`);
        if (movedRow) {
          movedRow.style.backgroundColor = '#e3f2fd';
          setTimeout(() => {
            movedRow.style.backgroundColor = '';
          }, 500);
        }
      }, 50);
    }
  };

  const handleMoveCombinedConditionUp = (mappingIndex: number, conditionIndex: number) => {
    if (conditionIndex > 0) {
      const updatedFields = [...mappingFields];
      const conditions = [...(updatedFields[mappingIndex].combinedConditions || [])];
      [conditions[conditionIndex - 1], conditions[conditionIndex]] = [conditions[conditionIndex], conditions[conditionIndex - 1]];
      updatedFields[mappingIndex] = {
        ...updatedFields[mappingIndex],
        combinedConditions: conditions
      };
      setMappingFields(updatedFields);
      
      // Visual feedback
      setTimeout(() => {
        const movedCondition = document.getElementById(`condition-${mappingIndex}-${conditionIndex - 1}`);
        if (movedCondition) {
          movedCondition.style.backgroundColor = '#e8f5e8';
          setTimeout(() => {
            movedCondition.style.backgroundColor = '#fafafa';
          }, 500);
        }
      }, 50);
    }
  };

  const handleMoveCombinedConditionDown = (mappingIndex: number, conditionIndex: number) => {
    const updatedFields = [...mappingFields];
    const conditions = [...(updatedFields[mappingIndex].combinedConditions || [])];
    if (conditionIndex < conditions.length - 1) {
      [conditions[conditionIndex], conditions[conditionIndex + 1]] = [conditions[conditionIndex + 1], conditions[conditionIndex]];
      updatedFields[mappingIndex] = {
        ...updatedFields[mappingIndex],
        combinedConditions: conditions
      };
      setMappingFields(updatedFields);
      
      // Visual feedback
      setTimeout(() => {
        const movedCondition = document.getElementById(`condition-${mappingIndex}-${conditionIndex + 1}`);
        if (movedCondition) {
          movedCondition.style.backgroundColor = '#e8f5e8';
          setTimeout(() => {
            movedCondition.style.backgroundColor = '#fafafa';
          }, 500);
        }
      }, 50);
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  useEffect(() => {
    if (editingMapping) {
      setDescription(editingMapping.description || '');
      setSelectedField(editingMapping.field);
      const fieldOptions = getFieldOptions().find(f => f.value === editingMapping.field)?.options || [];
      const mappings = Array.isArray(editingMapping.mappings) ? editingMapping.mappings : [];
      const fields: CustomMappingField[] = mappings.map(mapping => {
        const option = fieldOptions.find(opt => opt.value === mapping.value);
        const combinedConditions = mapping.extraFields?.map((field, index) => ({
          column: field,
          value: mapping.extraValues?.[index] || ''
        })) || [];
        
        return {
          field: editingMapping.field,
          value: mapping.field,
          mappedValue: mapping.value,
          originalMappedValue: mapping.value,
          mappedValueLabel: option ? option.label : mapping.value,
          combinedConditions
        };
      });
      setMappingFields(fields);
    } else {
      setDescription('');
      setSelectedField(field || '');
      setMappingFields([]);
    }
    const fieldEditing = getFieldOptions().find(f => f.value === editingMapping?.field);
    setIsActiveExtraCondition(fieldEditing?.activeExtraCondition || false);
  }, [editingMapping, field]);

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth={isFullscreen ? false : "xl"} 
        fullWidth
        fullScreen={isFullscreen}
        sx={{
          '& .MuiDialog-paper': {
            height: isFullscreen ? '100vh' : 'auto',
            maxHeight: isFullscreen ? '100vh' : '90vh',
            width: isFullscreen ? '100vw' : '95vw',
            maxWidth: isFullscreen ? '100vw' : '1400px'
          },
          '& @keyframes fadeIn': {
            from: { opacity: 0, transform: 'translateY(10px)' },
            to: { opacity: 1, transform: 'translateY(0)' }
          },
          '& @keyframes slideIn': {
            from: { opacity: 0, transform: 'translateX(-20px)' },
            to: { opacity: 1, transform: 'translateX(0)' }
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {editingMapping ? "Edit Cell Mapping" : "Create New Cell Mapping"}
          </Typography>
          <IconButton 
            onClick={toggleFullscreen}
            sx={{ ml: 2 }}
            title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Tabs value={activeTab} onChange={(_, newValue) => {setActiveTab(newValue);}} sx={{ mb: 2 }}>
            <Tab label="Existing Mappings" onClick={()=>{
              handleCancel();
            }} />
            <Tab label={editingMapping ? "Edit Cell Mapping" : "Create New Cell Mapping"} />
          </Tabs>

          {activeTab === 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Field</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.values(existingMappings).length > 0 ? Object.values(existingMappings).map((mapping) => (
                    <TableRow key={mapping.id}>
                      <TableCell>{getFieldLabel(mapping.field)}</TableCell>
                      <TableCell>{mapping.description}</TableCell>
                      <TableCell>
                        <IconButton onClick={() => handleEditMapping(mapping)}>
                          <EditIcon />
                        </IconButton>
                        {onDelete && (
                          <IconButton onClick={() => handleDeleteClick(mapping)}>
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  )) : <TableRow><TableCell align="center" colSpan={3}>No mappings found</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <>
              {!field && (
                <Box sx={{ mb: 2 }}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Field</InputLabel>
                    <Select
                      value={selectedField}
                      onChange={(e) => {
                        setSelectedField(e.target.value);
                        const fieldEditing = getFieldOptions().find(f => f.value === e.target.value);
                        setIsActiveExtraCondition(fieldEditing?.activeExtraCondition || false);
                      }}
                      label="Field"
                      disabled={!!editingMapping}
                    >
                      {getAvailableFields().map((field) => (
                        <MenuItem key={field.value} value={field.value}>
                          {field.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              <Box sx={{ mb: 2 }}>
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  margin="normal"
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddMapping}
                  disabled={!selectedField}
                >
                  Add Mapping
                </Button>
              </Box>

              <TableContainer 
                component={Paper} 
                sx={{ 
                  maxHeight: isFullscreen ? 'calc(100vh - 200px)' : 600,
                  height: isFullscreen ? 'calc(100vh - 200px)' : 'auto',
                  width: '100%',
                  overflowX: 'auto'
                }}
              >
                                  <Table size="small" stickyHeader sx={{ minWidth: '1200px' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell width={150} sx={{ py: 0.5, px: 1 }}>From value</TableCell>
                      {isActiveExtraCondition && (
                        <>
                          <TableCell sx={{ py: 0.5, px: 1 }}>Extra Column</TableCell>
                        </>
                      )}
                      <TableCell sx={{ py: 0.5, px: 1 }}>e-conomic Value</TableCell>
                      <TableCell width={120} sx={{ py: 0.5, px: 1 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mappingFields.map((field, index) => {
                      const fieldOptions = getFieldOptions().find(f => f.value === field.field);
               
                      const options = fieldOptions?.options || [];
                      const extraColumnsOptions = fieldOptions?.extrColumnsOptions || [];
                      return (
                        <TableRow 
                          key={index}
                          id={`mapping-row-${index}`}
                          sx={{
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.02)',
                              transform: 'scale(1.001)',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            },
                            position: 'relative',
                            animation: 'fadeIn 0.3s ease-in-out'
                          }}
                        >
                          <TableCell sx={{ py: 0.5, px: 1 }}>
                            <TextField
                              value={field.value}
                              onChange={(e) => handleUpdateMapping(index, 'value', e.target.value)}
                              size="small"
                              fullWidth
                              margin="none"
                              sx={{ ...formControlStyles, maxWidth: 140 }}
                            />
                          </TableCell>
                          {isActiveExtraCondition && (
                            <>
                              <TableCell sx={{ py: 0.5, px: 1 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleAddCombinedCondition(index)}
                                  sx={{ mb: 0.5, height: '28px', minWidth: '120px', padding: '0 8px' }}
                                >
                                  Add Condition
                                </Button>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  {field.combinedConditions?.map((condition, conditionIndex) => (
                                    <Box 
                                      key={conditionIndex}
                                      id={`condition-${index}-${conditionIndex}`}
                                      sx={{ 
                                        display: 'flex', 
                                        gap: 0.5, 
                                        alignItems: 'center',
                                        p: 1,
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 1,
                                        backgroundColor: '#fafafa',
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                          backgroundColor: '#f0f0f0',
                                          transform: 'translateX(2px)',
                                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        },
                                        position: 'relative',
                                        mb: 0.5,
                                        animation: 'slideIn 0.3s ease-in-out'
                                      }}
                                    >
                                      {/* Move buttons */}
                                      <Box sx={{ display: 'flex', flexDirection: 'column', mr: 0.5 }}>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleMoveCombinedConditionUp(index, conditionIndex)}
                                          disabled={conditionIndex === 0}
                                          sx={{ 
                                            height: '16px', 
                                            width: '16px', 
                                            padding: '1px',
                                            transition: 'all 0.2s ease-in-out',
                                            '&:hover': {
                                              backgroundColor: '#e8f5e8',
                                              transform: 'scale(1.2)',
                                              color: '#2e7d32'
                                            },
                                            '&:active': {
                                              transform: 'scale(0.9)'
                                            }
                                          }}
                                          title="Move condition up"
                                        >
                                          <ArrowUpIcon sx={{ fontSize: '12px' }} />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleMoveCombinedConditionDown(index, conditionIndex)}
                                          disabled={conditionIndex === (field.combinedConditions?.length || 0) - 1}
                                          sx={{ 
                                            height: '16px', 
                                            width: '16px', 
                                            padding: '1px',
                                            transition: 'all 0.2s ease-in-out',
                                            '&:hover': {
                                              backgroundColor: '#e8f5e8',
                                              transform: 'scale(1.2)',
                                              color: '#2e7d32'
                                            },
                                            '&:active': {
                                              transform: 'scale(0.9)'
                                            }
                                          }}
                                          title="Move condition down"
                                        >
                                          <ArrowDownIcon sx={{ fontSize: '12px' }} />
                                        </IconButton>
                                      </Box>

                                                                             {/* Column selector */}
                                      <FormControl size="small" margin="none" sx={{ ...formControlStyles, minWidth: 160 }}>
                                        <Select
                                          value={condition.column}
                                          onChange={(e) => handleUpdateCombinedCondition(index, conditionIndex, 'column', e.target.value)}
                                          size="small"
                                          displayEmpty
                                        >
                                          <MenuItem value="">
                                            <em>Select Column</em>
                                          </MenuItem>
                                          {extraColumnsOptions.map((column) => (
                                            <MenuItem key={column.value} value={column.value}>
                                              {column.label}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>

                                      {/* Operator selector */}
                                      <FormControl size="small" margin="none" sx={{ ...formControlStyles, minWidth: 120 }}>
                                        <Select
                                          value={
                                            condition.value === 'empty' ? 'empty' :
                                            condition.value === 'not_empty' ? 'not_empty' :
                                            condition.value === '*' ? '*' :
                                            condition.value && condition.value.startsWith('>') ? (condition.value.startsWith('>=') ? '>=' : '>') :
                                            condition.value && condition.value.startsWith('<') ? (condition.value.startsWith('<=') ? '<=' : '<') :
                                            (condition.value && condition.value !== 'empty' && condition.value !== 'not_empty' && condition.value !== '*' && (condition.value.includes('*') || condition.value.includes('?'))) ? 'custom_wildcard' :
                                            ''
                                          }
                                          onChange={(e) => {
                                            if (e.target.value === 'empty') {
                                              handleUpdateCombinedCondition(index, conditionIndex, 'value', 'empty');
                                            } else if (e.target.value === 'not_empty') {
                                              handleUpdateCombinedCondition(index, conditionIndex, 'value', 'not_empty');
                                            } else if (e.target.value === '*') {
                                              handleUpdateCombinedCondition(index, conditionIndex, 'value', '*');
                                            } else if (e.target.value === '>' || e.target.value === '<' || e.target.value === '>=' || e.target.value === '<=') {
                                              // Set operator prefix, user will input number after
                                              handleUpdateCombinedCondition(index, conditionIndex, 'value', e.target.value + '');
                                            } else if (e.target.value === 'custom_wildcard') {
                                              // Set to wildcard pattern if not already
                                              if (!condition.value || (!condition.value.includes('*') && !condition.value.includes('?'))) {
                                                handleUpdateCombinedCondition(index, conditionIndex, 'value', '*');
                                              }
                                            } else {
                                              // Equal case - clear the value to allow user input
                                              handleUpdateCombinedCondition(index, conditionIndex, 'value', '');
                                            }
                                          }}
                                          size="small"
                                          displayEmpty
                                        >
                                          {specialValueOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                              {option.label}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>

                                      {/* Value input */}
                                      <Tooltip
                                        title={
                                          <Box>
                                            <Typography variant="caption" display="block">Condition Types:</Typography>
                                            <Typography variant="caption" display="block">• Equal: exact match</Typography>
                                            <Typography variant="caption" display="block">• Wildcard: * = any chars, ? = single char</Typography>
                                            <Typography variant="caption" display="block">• Numeric: {">"}, {"<"}, {">="}, {"<="} for numbers</Typography>
                                            <Typography variant="caption" display="block">Examples: "test", "A*", {">"}100, {"<="}50.5</Typography>
                                          </Box>
                                        }
                                        arrow
                                        placement="top"
                                      >
                                        <TextField
                                          value={
                                            condition.value === 'empty' || condition.value === 'not_empty' ? '' :
                                            condition.value && (condition.value.startsWith('>') || condition.value.startsWith('<')) ? 
                                              condition.value.replace(/^(>=|<=|>|<)/, '') : 
                                            condition.value
                                          }
                                          onChange={(e) => {
                                            const currentOperator = 
                                              condition.value && condition.value.startsWith('>=') ? '>=' :
                                              condition.value && condition.value.startsWith('<=') ? '<=' :
                                              condition.value && condition.value.startsWith('>') ? '>' :
                                              condition.value && condition.value.startsWith('<') ? '<' : '';
                                            
                                            if (currentOperator) {
                                              handleUpdateCombinedCondition(index, conditionIndex, 'value', currentOperator + e.target.value);
                                            } else {
                                              handleUpdateCombinedCondition(index, conditionIndex, 'value', e.target.value);
                                            }
                                          }}
                                          size="small"
                                          placeholder={
                                            condition.value === 'empty' ? 'Field is empty' :
                                            condition.value === 'not_empty' ? 'Field has value' :
                                            condition.value && condition.value.startsWith('>') ? 'Enter number to compare' :
                                            condition.value && condition.value.startsWith('<') ? 'Enter number to compare' :
                                            'Value or pattern (* ? wildcards)'
                                          }
                                          disabled={condition.value === 'empty' || condition.value === 'not_empty'}
                                          margin="none"
                                          sx={{ ...formControlStyles, minWidth: 150, flex: 1 }}
                                          helperText={
                                            condition.value && condition.value !== 'empty' && condition.value !== 'not_empty' && 
                                            (condition.value.includes('*') || condition.value.includes('?')) ? 
                                            '* = any chars, ? = single char' : 
                                            condition.value && (condition.value.startsWith('>') || condition.value.startsWith('<')) ?
                                            'Numeric comparison (integers and decimals supported)' : null
                                          }
                                        />
                                      </Tooltip>

                                      {/* Delete button */}
                                      <IconButton
                                        size="small"
                                        onClick={() => handleRemoveCombinedCondition(index, conditionIndex)}
                                        sx={{ height: '24px', width: '24px', padding: '2px', ml: 0.5 }}
                                        title="Delete condition"
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  ))}
                                </Box>
                              </TableCell>
                            </>
                          )}
                          <TableCell sx={{ py: 0.5, px: 1 }}>
                            {options.length > 0 ? (
                              <FormControl fullWidth size="small" margin="none" sx={formControlStyles}>
                                <Select
                                  value={field.mappedValueLabel || field.mappedValue}
                                  onChange={(e) => handleUpdateMapping(index, 'mappedValue', e.target.value)}
                                  size="small"
                                >
                                  {options.map((option) => (
                                    <MenuItem key={option.value} value={option.label}>
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : (
                              <TextField
                                value={field.mappedValue}
                                onChange={(e) => handleUpdateMapping(index, 'mappedValue', e.target.value)}
                                size="small"
                                fullWidth
                                margin="none"
                                sx={formControlStyles}
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 0.5, px: 1 }}>
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                              <IconButton
                                size="small"
                                onClick={() => handleMoveMappingUp(index)}
                                disabled={index === 0}
                                sx={{ 
                                  height: '28px', 
                                  width: '28px', 
                                  padding: '4px',
                                  transition: 'all 0.2s ease-in-out',
                                  '&:hover': {
                                    backgroundColor: '#e3f2fd',
                                    transform: 'scale(1.1)',
                                    color: '#1976d2'
                                  },
                                  '&:active': {
                                    transform: 'scale(0.95)'
                                  }
                                }}
                                title="Move up"
                              >
                                <ArrowUpIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleMoveMappingDown(index)}
                                disabled={index === mappingFields.length - 1}
                                sx={{ 
                                  height: '28px', 
                                  width: '28px', 
                                  padding: '4px',
                                  transition: 'all 0.2s ease-in-out',
                                  '&:hover': {
                                    backgroundColor: '#e3f2fd',
                                    transform: 'scale(1.1)',
                                    color: '#1976d2'
                                  },
                                  '&:active': {
                                    transform: 'scale(0.95)'
                                  }
                                }}
                                title="Move down"
                              >
                                <ArrowDownIcon fontSize="small" />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                onClick={() => handleRemoveMapping(index)}
                                sx={{ height: '28px', width: '28px', padding: '4px' }}
                                title="Delete"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={activeTab === 0 ? handleClose : handleCancel}>Cancel</Button>
          {activeTab === 1 && (
            <Button onClick={handleSave} disabled={isUpdating}>
              {isUpdating ? <CircularProgress size={20} /> : editingMapping ? "Update" : "Save"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this mapping?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CustomMappingManager;
